import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  TShirt,
  CalendarCheck
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import InfoTip from '../../components/InfoTip';

export default function WardrobePlanner() {
  const { currentEvent } = useAuth();
  const itinerary = currentEvent?.itinerary || [];

  // Group events by dress code
  const eventsByDressCode = itinerary.reduce((acc, item) => {
    const code = item.dress_code || 'No specific dress code';
    if (!acc[code]) acc[code] = [];
    acc[code].push(item);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-1">
          <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="wardrobe-title">Wardrobe Planner</h1>
          <InfoTip content="Plan your outfits for each event." label="About the wardrobe planner" />
        </div>

        {/* Events by Dress Code */}
        {Object.entries(eventsByDressCode).map(([dressCode, events]) => (
          <Card key={dressCode}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TShirt className="w-5 h-5 text-wedding-gold" />
                {dressCode}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {events.map((event) => (
                  <div 
                    key={event.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-wedding-sage/5"
                    data-testid={`wardrobe-event-${event.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <CalendarCheck className="w-5 h-5 text-wedding-sage" />
                      <div>
                        <p className="font-medium">{event.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(event.date), 'EEEE, MMM d')} at {event.start_time}
                        </p>
                      </div>
                    </div>
                    {event.venue?.name && (
                      <Badge variant="outline">{event.venue.name}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {itinerary.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <TShirt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No events scheduled yet. Once events are added, you'll see outfit suggestions here.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Packing Tips */}
        <Card className="border-wedding-gold/20 bg-wedding-gold/5">
          <CardContent className="p-4">
            <h3 className="font-serif font-semibold mb-2 flex items-center gap-2">
              <TShirt className="w-5 h-5 text-wedding-gold" />
              Packing Tips
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Check the dress code for each event before packing</li>
              <li>Bring comfortable shoes for outdoor venues</li>
              <li>Consider the weather forecast for the event dates</li>
              <li>Pack a steamer or request iron from the hotel</li>
              <li>Bring accessories that can work with multiple outfits</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
